import { copyEconomyTemplate, deleteEconomyTemplate, getEconomyTemplates } from '@/api/economyTemplates';
import { EconomyTemplate } from '@/types/economyTemplate';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import ConfirmDeleteDialog from '../common/ConfirmDeleteDialog';
import CreateTemplateModal from './CreateTemplateModal';
import TemplateCard from './TemplateCard';
import TemplateDetailModal from './TemplateDetailModal';

const TemplatesList: React.FC = () => {
  const [templates, setTemplates] = useState<EconomyTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState<'name' | 'description'>('name');
  const [publicFilter, setPublicFilter] = useState<'all' | 'public' | 'private'>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'field' | 'layer'>('all');
  const [selectedTemplate, setSelectedTemplate] = useState<EconomyTemplate | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<EconomyTemplate | null>(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getEconomyTemplates({ limit: 200 });
      setTemplates(response.templates);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to load economy templates');
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = templates.filter((template) => {
    const normalizedSearch = searchTerm.toLowerCase().trim();
    const matchesSearch =
      !normalizedSearch ||
      (searchField === 'name'
        ? template.name.toLowerCase().includes(normalizedSearch)
        : (template.description || '').toLowerCase().includes(normalizedSearch));
    const matchesPublic =
      publicFilter === 'all' ||
      (publicFilter === 'public' && template.is_public) ||
      (publicFilter === 'private' && !template.is_public);
    const matchesType = typeFilter === 'all' || template.template_type === typeFilter;

    return matchesSearch && matchesPublic && matchesType;
  });

  const handleTemplateClick = (template: EconomyTemplate) => {
    setSelectedTemplate(template);
    setIsDetailModalOpen(true);
  };

  const handleTemplateEdit = (template: EconomyTemplate) => {
    setSelectedTemplate(template);
    setIsDetailModalOpen(true);
  };

  const handleTemplateCopy = async (template: EconomyTemplate) => {
    try {
      setLoading(true);
      await copyEconomyTemplate(template.id);
      await fetchTemplates();
    } catch (err) {
      console.error('Error copying template:', err);
      setError('Failed to copy template');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateDelete = (template: EconomyTemplate) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;

    try {
      setLoading(true);
      await deleteEconomyTemplate(templateToDelete.id);
      await fetchTemplates();
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    } catch (err) {
      console.error('Error deleting template:', err);
      setError('Failed to delete template');
    } finally {
      setLoading(false);
    }
  };

  if (loading && templates.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading economy templates...
        </Typography>
      </Box>
    );
  }

  if (error && templates.length === 0) {
    return (
      <Alert
        severity="error"
        sx={{ mb: 3 }}
        action={
          <Typography
            component="button"
            onClick={fetchTemplates}
            sx={{
              background: 'none',
              border: 'none',
              color: 'inherit',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            Try again
          </Typography>
        }
      >
        <AlertTitle>Error</AlertTitle>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsCreateModalOpen(true)}
        >
          Create Template
        </Button>
      </Box>

      <Box sx={{ mb: 4, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Search in</InputLabel>
          <Select
            value={searchField}
            onChange={(e) => setSearchField(e.target.value as 'name' | 'description')}
            label="Search in"
          >
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="description">Description</MenuItem>
          </Select>
        </FormControl>

        <TextField
          placeholder={searchField === 'name' ? 'Search by name...' : 'Search by description...'}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          sx={{ minWidth: 300 }}
          size="small"
        />

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by visibility</InputLabel>
          <Select
            value={publicFilter}
            onChange={(e) => setPublicFilter(e.target.value as 'all' | 'public' | 'private')}
            label="Filter by visibility"
          >
            <MenuItem value="all">All templates</MenuItem>
            <MenuItem value="public">Public only</MenuItem>
            <MenuItem value="private">Private only</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by type</InputLabel>
          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as 'all' | 'field' | 'layer')}
            label="Filter by type"
          >
            <MenuItem value="all">All types</MenuItem>
            <MenuItem value="field">Field economics</MenuItem>
            <MenuItem value="layer">Layer economics</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {filteredTemplates.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchTerm || publicFilter !== 'all' || typeFilter !== 'all'
              ? 'No economy templates match your filters'
              : 'No economy templates found'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm || publicFilter !== 'all' || typeFilter !== 'all'
              ? 'Try adjusting your search criteria or filters'
              : 'Create your first economy template to get started'}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredTemplates.map((template) => (
            <Grid item key={template.id} xs={12} sm={6} md={4} lg={3}>
              <TemplateCard
                template={template}
                onClick={handleTemplateClick}
                onEdit={handleTemplateEdit}
                onCopy={handleTemplateCopy}
                onDelete={handleTemplateDelete}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Box sx={{ mt: 3, textAlign: 'right' }}>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredTemplates.length} of {templates.length} economy templates
        </Typography>
      </Box>

      <TemplateDetailModal
        open={isDetailModalOpen}
        templateId={selectedTemplate?.id || null}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedTemplate(null);
        }}
        onTemplateUpdated={() => {
          fetchTemplates();
        }}
      />

      <CreateTemplateModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          fetchTemplates();
          setIsCreateModalOpen(false);
        }}
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setTemplateToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Economy Template"
        itemName={templateToDelete?.name}
        itemType="economy template"
        loading={loading}
      />
    </Box>
  );
};

export default TemplatesList;
